import axiosInstance from '../api/axios';
import type {
  GetMerchantFakeCheckStatisticsParams,
  GetMerchantsParams,
  MerchantFakeCheckStatistics,
} from '@/types/merchant';

const DEFAULT_LIST_LIMIT = 300;

export const merchantsService = {
  getTransactionOptions: async (): Promise<{ id: string; name: string | null }[]> => {
    const response = await axiosInstance.get('/transactions/merchant-options', {
      params: { limit: DEFAULT_LIST_LIMIT },
    });
    return response.data;
  },
  getMerchants: async (params: GetMerchantsParams = {}) => {
    const response = await axiosInstance.get('/merchants', {
      params: {
        limit: DEFAULT_LIST_LIMIT,
        ...params,
      },
    });
    return response.data;
  },

  getMerchant: async (id: string) => {
    const response = await axiosInstance.get(`/merchants/${id}`);
    return response.data;
  },

  getFakeCheckStatistics: async (
    params: GetMerchantFakeCheckStatisticsParams,
  ): Promise<MerchantFakeCheckStatistics[]> => {
    const response = await axiosInstance.get('/merchants/statistics/fake-checks', { params });
    return response.data;
  },

  getTelegramChats: async (id: string) => {
    const response = await axiosInstance.get(`/merchants/${id}/telegram-chats`);
    return response.data;
  },
  updateTelegramChatLink: async (merchantId: string, chatId: string, inviteUrl: string | null) =>
    (await axiosInstance.patch(`/merchants/${merchantId}/telegram-chats/${chatId}`, { invite_url: inviteUrl })).data,
  createTelegramChat: async (merchantId: string, data: { chat_kind: string; title: string; invite_url: string }) =>
    (await axiosInstance.post(`/merchants/${merchantId}/telegram-chats`, data)).data,
  updateTelegramChat: async (merchantId: string, chatId: string, data: { chat_kind: string; title: string; invite_url: string }) =>
    (await axiosInstance.put(`/merchants/${merchantId}/telegram-chats/${chatId}`, data)).data,

  createMerchant: async (data: any) => {
    const response = await axiosInstance.post('/merchants', data);
    return response.data;
  },

  updateMerchant: async (id: string, data: any) => {
    const response = await axiosInstance.put(`/merchants/${id}`, data);
    return response.data;
  },

  deleteMerchant: async (id: string) => {
    const response = await axiosInstance.delete(`/merchants/${id}`);
    return response.data;
  },

  getMerchantRates: async (id: string) => {
    const response = await axiosInstance.get(`/merchants/${id}/rates`);
    return response.data;
  },

  updateMerchantRates: async (id: string, data: any) => {
    const response = await axiosInstance.put(`/merchants/${id}/rates`, data);
    return response.data;
  },

  getMerchantBalances: async (id: string) => {
    const response = await axiosInstance.get(`/merchants/${id}/balances`);
    return response.data;
  },

  getAllMerchantBalances: async () => {
    const response = await axiosInstance.get('/merchants/balances/');
    return response.data;
  },

  createMerchantBalance: async (data: any) => {
    const response = await axiosInstance.post('/merchants/balances', data);
    return response.data;
  },

  updateMerchantBalance: async (id: string, data: any) => {
    const response = await axiosInstance.put(`/merchants/balances/${id}`, data);
    return response.data;
  },

  deleteMerchantBalance: async (id: string) => {
    const response = await axiosInstance.delete(`/merchants/balances/${id}`);
    return response.data;
  },

  createMerchantRate: async (data: any) => {
    const response = await axiosInstance.post('/merchant_rates/', data);
    return response.data;
  },

  updateMerchantRate: async (id: string, data: any) => {
    const response = await axiosInstance.put(`/merchant_rates/${id}`, data);
    return response.data;
  },

  deleteMerchantRate: async (id: string) => {
    const response = await axiosInstance.delete(`/merchant_rates/${id}`);
    return response.data;
  },

  toggleMerchantRateActive: async (id: string) => {
    const response = await axiosInstance.patch(`/merchant_rates/${id}/toggle-active`);
    return response.data;
  },

  getAllMerchantRates: async () => {
    const response = await axiosInstance.get('/merchant_rates/');
    return response.data;
  },

  getSandboxRequisites: async () => {
    const response = await axiosInstance.get('/sandbox-requisites/');
    return response.data;
  },

  createSandboxRequisite: async (data: any) => {
    const response = await axiosInstance.post('/sandbox-requisites/', data);
    return response.data;
  },

  updateSandboxRequisite: async (id: string, data: any) => {
    const response = await axiosInstance.put(`/sandbox-requisites/${id}`, data);
    return response.data;
  },

  deleteSandboxRequisite: async (id: string) => {
    await axiosInstance.delete(`/sandbox-requisites/${id}`);
  },
};
